/**
 * Conversation history — the shapes the sidebar needs, and the grouping it
 * renders.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * HISTORY IS SERVER STATE. THIS FILE DEFINES A PORT, NOT A STORE.
 *
 * A thread's title, its pinned flag and its position in "Today / Yesterday /
 * Previous 7 days" are things a person expects to survive a new laptop, a
 * private window and a cleared cache. Keeping any of it in `localStorage`
 * would make history a property of ONE BROWSER — pins that vanish on a phone,
 * titles that disagree between tabs, an "Older" group that resets itself. So
 * nothing here persists anything; every mutation is a call the server answers,
 * and the list is re-read from the server after each one.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE PORT IS BOUND. It is implemented in `threadsClient.ts` over
 * `intelligenceThreads` in the api-client, against the six routes
 * `server.ts` mounts.
 *
 * WHAT CHANGED WHEN IT WAS BOUND, because the port was written first and
 * guessed one thing wrong. `IntelligenceThread` used to carry a `sessionId`,
 * on the reasoning that `sessionId` was "the ONLY continuity handle
 * `turnRequestSchema` has" and a durable thread therefore had to carry the
 * session it resumed. The shipped API does not work that way and has no such
 * field: a thread is identified by its own `threadId`, `turnBodySchema` takes
 * an OPTIONAL `threadId` alongside `sessionId`, and the two mean different
 * things — `sessionId` keys the in-memory transcript the MODEL sees, while
 * `threadId` keys the durable record of what the PERSON saw. Carrying a
 * session id per thread would have been a second, browser-authored answer to a
 * question the server already answers, so it is gone rather than adapted.
 */

/**
 * One conversation, as the sidebar needs it.
 *
 * `title` is SERVER-AUTHORED. The sidebar renders it and never derives one
 * from the first message: a title computed on the client would differ between
 * the browser that created the thread and every browser that later lists it.
 *
 * `updatedAtMs` is the SERVER's clock, and it is what the date grouping reads.
 * Grouping on a client clock would put a thread in "Yesterday" for a user
 * whose laptop is a day fast.
 */
export interface IntelligenceThread {
  readonly threadId: string;
  readonly title: string;
  readonly pinned: boolean;
  readonly updatedAtMs: number;
}

/**
 * The durable calls this sidebar makes. Implemented by L141's api-client
 * module; see `threadsClient.ts` for the single binding site.
 *
 * Every mutation resolves to the server's own view of the row rather than to
 * void, so the list can be reconciled from the response instead of from what
 * the client hoped would happen.
 */
export interface ThreadsClient {
  /** Every non-archived thread for the signed-in caller, newest first. */
  list(): Promise<{ readonly threads: readonly IntelligenceThread[] }>;
  /**
   * Starts a conversation from its first message, so the turn that follows has
   * something to be appended to.
   *
   * The TITLE IS THE SERVER'S. Supplying `firstMessage` makes the server
   * derive one deterministically, with no model call; the alternative — a
   * title computed here — would differ between the browser that created the
   * thread and every browser that later lists it. There is deliberately no
   * placeholder: this product never shows a conversation called "New
   * conversation".
   */
  create(input: { firstMessage: string }): Promise<IntelligenceThread>;
  /** The stored transcript, oldest first — what the PERSON saw, replayed. */
  messages(input: { threadId: string }): Promise<{ readonly messages: readonly ThreadMessage[] }>;
  rename(input: { threadId: string; title: string }): Promise<IntelligenceThread>;
  setPinned(input: { threadId: string; pinned: boolean }): Promise<IntelligenceThread>;
  /** Removes from the list, keeps the record. Reversible server-side. */
  archive(input: { threadId: string }): Promise<void>;
  /** Destroys the thread and its turns. Confirmed in the UI before it is called. */
  remove(input: { threadId: string }): Promise<void>;
}

/**
 * One half of a stored exchange, as it is replayed into a reopened
 * conversation.
 *
 * `body` is the user-visible text and NOTHING ELSE — what the person typed, or
 * the final answer they read. It is never an intermediate tool-calling
 * message, a raw tool result or a reasoning trace: the server has no column
 * for those and the append path has no parameter for them.
 *
 * The evidence manifest and the typed result objects the answer was rendered
 * with ARE stored, and are deliberately not read here. Re-rendering a
 * dashboard preview from an earlier version of the server means parsing a
 * payload with today's schemas and drawing live action buttons over a
 * yesterday's plan; a replayed transcript is a record of what was said, and
 * this surface says so rather than reconstructing an interactive answer it
 * cannot vouch for.
 */
export interface ThreadMessage {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly body: string;
}

// ---------------------------------------------------------------------------
// Grouping.
// ---------------------------------------------------------------------------

/**
 * The groups, in the order they render. PINNED IS FIRST AND IS NOT A DATE
 * GROUP: a pinned thread is one the person chose to keep in reach, and letting
 * it drift down into "Previous 30 days" as it ages would defeat the pin. A
 * pinned thread therefore appears in `Pinned` and NOWHERE ELSE — listing it
 * twice makes two rows that rename and delete the same thing.
 */
export const THREAD_GROUP_ORDER = [
  'Pinned',
  'Today',
  'Yesterday',
  'Previous 7 days',
  'Previous 30 days',
  'Older',
] as const;

export type ThreadGroupLabel = (typeof THREAD_GROUP_ORDER)[number];

export interface ThreadGroup {
  readonly label: ThreadGroupLabel;
  readonly threads: readonly IntelligenceThread[];
}

/**
 * Whole days between two instants, counted in CALENDAR days on the reader's
 * own clock rather than in 86_400_000ms slices.
 *
 * The difference matters at exactly the moment a person notices: a thread from
 * 11pm last night is "Yesterday" at 8am, even though it is nine hours old, and
 * a thread from 1am this morning is "Today" at 11pm even though it is
 * twenty-two hours old. Elapsed-milliseconds arithmetic gets both wrong, and
 * gets them wrong in the way that makes a sidebar feel broken.
 */
function calendarDaysBetween(earlierMs: number, laterMs: number): number {
  const startOfDay = (ms: number): number => {
    const date = new Date(ms);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  };
  const diff = startOfDay(laterMs) - startOfDay(earlierMs);
  return Math.round(diff / 86_400_000);
}

/** Which date group a thread falls in, given the reader's "now". */
function dateGroupFor(thread: IntelligenceThread, nowMs: number): ThreadGroupLabel {
  const days = calendarDaysBetween(thread.updatedAtMs, nowMs);
  // A thread whose server timestamp is slightly ahead of the reader's clock
  // (a fast server, a slow laptop) yields a negative day count. It is Today,
  // not Older — clock skew must never file a live conversation under "Older".
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return 'Previous 7 days';
  if (days <= 30) return 'Previous 30 days';
  return 'Older';
}

/**
 * The sidebar's list: pinned first, then the date groups in order, each
 * newest-first, with empty groups omitted entirely.
 *
 * Sorting is done here rather than trusted from the wire. The server returns
 * threads newest-first, but a rename or a pin resolves one row at a time and
 * the list is reconciled locally between reads; a group that silently depended
 * on wire order would reorder itself the moment somebody renamed something.
 */
export function groupThreads(threads: readonly IntelligenceThread[], nowMs: number): ThreadGroup[] {
  const byLabel = new Map<ThreadGroupLabel, IntelligenceThread[]>();
  for (const thread of threads) {
    const label = thread.pinned ? 'Pinned' : dateGroupFor(thread, nowMs);
    const bucket = byLabel.get(label);
    if (bucket === undefined) byLabel.set(label, [thread]);
    else bucket.push(thread);
  }
  const out: ThreadGroup[] = [];
  for (const label of THREAD_GROUP_ORDER) {
    const bucket = byLabel.get(label);
    if (bucket === undefined || bucket.length === 0) continue;
    out.push({
      label,
      threads: [...bucket].sort((a, b) => b.updatedAtMs - a.updatedAtMs),
    });
  }
  return out;
}

/**
 * The threads a search box's text matches, by title, case-insensitively.
 *
 * A TRANSIENT FILTER, not stored state: it narrows what is on screen for as
 * long as the box has text in it and is forgotten on navigation. That is the
 * one kind of history-adjacent state a browser may legitimately own, because
 * losing it costs nothing — unlike a pin, which is a decision.
 *
 * Blank (or whitespace-only) input matches everything rather than nothing, so
 * clearing the box restores the full list.
 */
export function searchThreads(
  threads: readonly IntelligenceThread[],
  query: string,
): IntelligenceThread[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [...threads];
  return threads.filter((thread) => thread.title.toLowerCase().includes(needle));
}
